<template>
  <div class="account container-fluid">
    <div class="row header-row">
      <div class="col-12 col-lg-6 offset-lg-3">
        <h1>My Account</h1>
      </div>
    </div>
    <div
      v-if="$store.getters.mfaIsRequired"
      class="row"
    >
      <div class="col-12 col-lg-6 offset-lg-3">
        <div class="inline-alert">
          You must set up Multi-Factor Authentication to secure your account.
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-lg-6 offset-lg-3">
        <div class="card">
          <h4>Account Info</h4>
          <form-component>
            <form
              id="update-account"
              data-vv-scope="update-account"
              novalidate="novalidate"
              @submit.prevent="doUpdateAccount"
            >
              <input-text
                id="email"
                v-model="email"
                name="email"
                data-vv-name="email"
                data-vv-as="email"
                label="Email"
                type="text"
                :disabled="true"
              />

              <input-text
                id="full_name"
                v-model="full_name"
                v-validate="'required'"
                name="full_name"
                data-vv-name="full_name"
                data-vv-as="full name"
                label="Full Name"
                type="text"
                required
                :error="errors.first('update-account.full_name')"
              />

              <MazPhoneNumberInput
                :class="{'show-invalid': attemptedSave && !validPhoneNumber}"
                :default-country-code="phone_country_code"
                :default-phone-number="initial_phone"
                :success="false"
                :translations="{
                  phoneNumberLabel: 'Mobile Phone Number',
                }"
                :translate="{phoneNumberLabel: 'Mobile Phone Number'}"
                @update="updatePhoneNumber($event)"
              />
              <p
                v-show="attemptedSave && !validPhoneNumber"
                class="form__error"
              >
                The Mobile Phone Number field is required and must be valid.
              </p>

              <button
                :disabled="saving || resetting"
                class="btn btn-primary btn-large"
                type="submit"
              >
                <font-awesome-icon
                  v-if="saving"
                  :spin="true"
                  :icon="['fas', 'spinner']"
                  class="icon alt"
                />
                <span v-else>Save</span>
              </button>
            </form>
          </form-component>
        </div>
        <div class="card">
          <h4>Multi-Factor Authentication</h4>
          <h5>
            SMS Authentication
            <span
              v-if="($store.getters.mfaResponse
                && $store.getters.mfaResponse.results
                && $store.getters.mfaResponse.results.find(x => x.name === mfaType.sms))"
            >
              <div
                v-if="mfaType.sms === primaryMfaMethod"
                class="primary-method"
              >
                Primary
              </div>
              <span
                v-else
              >
                <button
                  class="second-method ml-1"
                  @click="changeActiveMethod(mfaType.sms)"
                >Make Primary</button>
                <button
                  class="second-method"
                  @click="deactivateMfaMethod(mfaType.sms)"
                >Disable</button>
              </span>
            </span>
          </h5>
          <div
            v-if="($store.getters.mfaResponse
              && $store.getters.mfaResponse.results
              && $store.getters.mfaResponse.results.find(x => x.name === mfaType.sms))"
          >
            SMS Multi-Factor Authentication method is activated for account.
          </div>
          <div v-else-if="!mfaActivateRequestText">
            <div>
              Verify mobile phone number above and make changes if needed.
              <br>
              Then begin activation by clicking the button down below. Msg & data rates may apply.
            </div>
            <br>
            <button
              :disabled="saving || resetting"
              class="btn btn-primary btn-large"
              type="button"
              @click="doMfaActivate()"
            >
              <font-awesome-icon
                v-if="saving"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Request Code to {{ smsPhoneNumber }}</span>
            </button>
          </div>
          <div v-else>
            <form-component>
              <form
                id="mfa-confirm"
                data-vv-scope="mfa-confirm"
                novalidate="novalidate"
                @submit.prevent="doMfaConfirm"
              >
                <div>{{ mfaActivateRequestText }}</div>
                <input-text
                  id="mfaCodeSms"
                  v-model="mfaCodeSms"
                  v-validate="'required'"
                  name="mfaCodeSms"
                  data-vv-name="mfaCodeSms"
                  data-vv-as="MFA Code"
                  label="MFA Code"
                  type="number"
                  :autocomplete="false"
                  :error="errors.first('mfa-confirm.mfaCodeSms')"
                />

                <button
                  :disabled="saving || resetting"
                  class="btn btn-primary btn-large"
                  type="submit"
                >
                  <font-awesome-icon
                    v-if="saving"
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                  <span v-else>Validate</span>
                </button>

                <button
                  :disabled="saving || resetting"
                  class="btn btn-large"
                  type="button"
                  @click="cancelMfa()"
                >
                  <font-awesome-icon
                    v-if="saving"
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                  <span v-else>Cancel</span>
                </button>
              </form>
            </form-component>
          </div>

          <hr>

          <h5>
            Yubikey Authentication
            <span
              v-if="($store.getters.mfaResponse
                && $store.getters.mfaResponse.results
                && $store.getters.mfaResponse.results.find(x => x.name === mfaType.yubikey))"
            >
              <div
                v-if="mfaType.yubikey === primaryMfaMethod"
                class="primary-method"
              >
                Primary
              </div>
              <span
                v-else
              >
                <button
                  class="second-method ml-1"
                  @click="changeActiveMethod(mfaType.yubikey)"
                >Make Primary</button>
                <button
                  class="second-method"
                  @click="deactivateMfaMethod(mfaType.yubikey)"
                >Disable</button>
              </span>
            </span>
          </h5>
          <div
            v-if="($store.getters.mfaResponse
              && $store.getters.mfaResponse.results
              && $store.getters.mfaResponse.results.find(x => x.name === mfaType.yubikey))"
          >
            Yubikey Multi-Factor Authentication method is activated for account.
          </div>
          <div v-else>
            <form-component>
              <form
                id="mfa-confirm-yubi"
                data-vv-scope="mfa-confirm-yubi"
                novalidate="novalidate"
                @submit.prevent="doMfaConfirmYubi"
              >
                <div>
                  To configure your Yubikey click in the text
                  field below and press the Yubikey button.
                </div>
                <input-text
                  id="mfaCodeYubi"
                  v-model="mfaCodeYubi"
                  v-validate="'required'"
                  name="mfaCodeYubi"
                  data-vv-name="mfaCodeYubi"
                  data-vv-as="Yubikey Code"
                  label="Yubikey Code"
                  type="text"
                  :autocomplete="false"
                  :error="errors.first('mfa-confirm-yubi.mfaCodeYubi')"
                />

                <button
                  :disabled="saving || resetting"
                  class="btn btn-primary btn-large"
                  type="submit"
                >
                  <font-awesome-icon
                    v-if="saving"
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                  <span v-else>Activate</span>
                </button>
              </form>
            </form-component>
          </div>

          <hr>

          <h5>
            Email Authentication
            <span
              v-if="($store.getters.mfaResponse
                && $store.getters.mfaResponse.results
                && $store.getters.mfaResponse.results.find(x => x.name === mfaType.email))"
            >
              <div
                v-if="mfaType.email === primaryMfaMethod"
                class="primary-method"
              >
                Primary
              </div>
              <span
                v-else
              >
                <button
                  class="second-method ml-1"
                  @click="changeActiveMethod(mfaType.email)"
                >Make Primary</button>
                <button
                  class="second-method"
                  @click="deactivateMfaMethod(mfaType.email)"
                >Disable</button>
              </span>
            </span>
          </h5>
          <div
            v-if="($store.getters.mfaResponse
              && $store.getters.mfaResponse.results
              && $store.getters.mfaResponse.results.find(x => x.name === mfaType.email))"
          >
            Email Multi-Factor Authentication method is activated for account.
          </div>
          <div v-else-if="!mfaActivateEmailRequestText">
            <div>
              Verify email above and make changes if needed.
              <br>
              Then begin activation by clicking the button down below to send email.
            </div>
            <br>
            <button
              :disabled="saving || resetting"
              class="btn btn-primary btn-large"
              type="button"
              @click="doMfaActivateEmail()"
            >
              <font-awesome-icon
                v-if="saving"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Request Code to {{ email }}</span>
            </button>
          </div>
          <div v-else>
            <form-component>
              <form
                id="email-confirm"
                data-vv-scope="email-confirm"
                novalidate="novalidate"
                @submit.prevent="doMfaConfirmEmail"
              >
                <div>{{ mfaActivateEmailRequestText }}</div>
                <input-text
                  id="mfaCodeEmail"
                  v-model="mfaCodeEmail"
                  v-validate="'required'"
                  name="mfaCodeEmail"
                  data-vv-name="mfaCodeEmail"
                  data-vv-as="MFA Code"
                  label="MFA Code"
                  type="number"
                  :autocomplete="false"
                  :error="errors.first('email-confirm.mfaCodeEmail')"
                />

                <button
                  :disabled="saving || resetting"
                  class="btn btn-primary btn-large"
                  type="submit"
                >
                  <font-awesome-icon
                    v-if="saving"
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                  <span v-else>Validate</span>
                </button>

                <button
                  :disabled="saving || resetting"
                  class="btn btn-large"
                  type="button"
                  @click="cancelMfa()"
                >
                  <font-awesome-icon
                    v-if="saving"
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                  <span v-else>Cancel</span>
                </button>
              </form>
            </form-component>
          </div>

          <hr>

          <h5>
            Authenticator Application Authentication
            <span
              v-if="($store.getters.mfaResponse
                && $store.getters.mfaResponse.results
                && $store.getters.mfaResponse.results.find(x => x.name === mfaType.app))"
            >
              <div
                v-if="mfaType.app === primaryMfaMethod"
                class="primary-method"
              >
                Primary
              </div>
              <span
                v-else
              >
                <button
                  class="second-method ml-1"
                  @click="changeActiveMethod(mfaType.app)"
                >Make Primary</button>
                <button
                  class="second-method"
                  @click="deactivateMfaMethod(mfaType.app)"
                >Disable</button>
              </span>
            </span>
          </h5>
          <div
            v-if="($store.getters.mfaResponse
              && $store.getters.mfaResponse.results
              && $store.getters.mfaResponse.results.find(x => x.name === mfaType.app))"
          >
            Authenticator Application Multi-Factor Authentication method is activated for account.
          </div>
          <div v-else-if="!mfaActivateAppRequestText">
            <div>
              Begin activation by clicking the button down below to display
              code to use within mobile authenticator application.
            </div>
            <br>
            <button
              :disabled="saving || resetting"
              class="btn btn-primary btn-large"
              type="button"
              @click="doMfaActivateApp()"
            >
              <font-awesome-icon
                v-if="saving"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <span v-else>Request Code</span>
            </button>
          </div>
          <div v-else>
            <form-component>
              <form
                id="app-confirm"
                data-vv-scope="app-confirm"
                novalidate="novalidate"
                @submit.prevent="doMfaConfirmApp"
              >
                <div>{{ mfaActivateAppRequestText }}</div>
                <div v-if="mfaQrCode">
                  <QrcodeVue
                    :value="mfaQrCode"
                    size="300"
                    level="H"
                  />
                </div>
                <input-text
                  id="mfaCodeApp"
                  v-model="mfaCodeApp"
                  v-validate="'required'"
                  name="mfaCodeApp"
                  data-vv-name="mfaCodeApp"
                  data-vv-as="MFA Code"
                  label="MFA Code"
                  type="number"
                  :autocomplete="false"
                  :error="errors.first('email-confirm.mfaCodeApp')"
                />

                <button
                  :disabled="saving || resetting"
                  class="btn btn-primary btn-large"
                  type="submit"
                >
                  <font-awesome-icon
                    v-if="saving"
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                  <span v-else>Validate</span>
                </button>

                <button
                  :disabled="saving || resetting"
                  class="btn btn-large"
                  type="button"
                  @click="cancelMfa()"
                >
                  <font-awesome-icon
                    v-if="saving"
                    :spin="true"
                    :icon="['fas', 'spinner']"
                    class="icon alt"
                  />
                  <span v-else>Cancel</span>
                </button>
              </form>
            </form-component>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import UserClient from '@/api/UserClient';
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';
// eslint-disable-next-line import/order
import QrcodeVue from 'qrcode.vue';
import MfaType from '../constants/mfaMethods';

export default {
  name: 'Account',
  components: {
    FormComponent,
    InputText,
    QrcodeVue,
  },
  props: {
    doCancel: {
      type: Function,
    },
    doSave: {
      type: Function,
    },
  },
  data() {
    return {
      userClient: new UserClient(this.$http),
      email: '',
      full_name: '',
      phone_number: '',
      initial_phone: this.$store.getters.currentUser.phone_number,
      attemptedSave: false,
      phone_country_code: this.$store.getters.currentUser.phone_country_code,
      phone_number_e164: this.$store.getters.currentUser.phone_number_e164,
      validPhoneNumber: false,

      mfaActivateRequestText: null,
      mfaActivateEmailRequestText: null,
      mfaActivateAppRequestText: null,
      mfaQrCode: null,
      smsPhoneNumber: '',
      mfaCodeYubi: '',
      mfaCodeSms: '',
      mfaCodeApp: '',
      mfaCodeEmail: '',

      saving: false,
      resetting: false,
      mfaType: MfaType,
      primaryMfaMethod: '',
      moreThanOneMfaMethod: false,
    };
  },
  mounted() {
    this.full_name = this.$store.getters.currentUser.full_name;
    this.phone_number = this.$store.getters.currentUser.phone_number;

    this.email = this.$store.getters.currentUser.email;
    this.smsPhoneNumber = this.phone_number.toString();
    this.populatePrimaryMfaMethod();
    setTimeout(() => {
      this.populatePrimaryMfaMethod();
    }, 3000);
  },
  methods: {
    updatePhoneNumber(phoneEvent) {
      this.validPhoneNumber = phoneEvent.isValid;
      this.phone_number = phoneEvent.formatNational;
      this.phone_country_code = phoneEvent.countryCode;
      this.phone_number_e164 = phoneEvent.e164;
    },
    doUpdateAccount() {
      this.attemptedSave = true;
      this.$validator.validateAll('update-account').then((result) => {
        if (result && this.validPhoneNumber) {
          this.errors.clear();
          this.saving = true;

          this.userClient.updateProfile(
            this.full_name, this.phone_number,
            this.phone_country_code, this.phone_number_e164,
          ).then(() => {
            this.saving = false;
            this.$store.commit('setGlobalAlert', {
              msg: 'Profile Saved!',
            });

            // update our cache todo: should probably update from api response here, just in case
            const user = this.$store.getters.currentUser;
            user.full_name = this.full_name;
            user.phone_number = this.phone_number;
            this.smsPhoneNumber = this.phone_number.toString();
            this.$store.commit('setUser', user);
          }).catch(() => {
            this.saving = false;
            this.$store.commit('setGlobalAlert', {
              msg: 'Unable to save profile. Please try again!', error: true,
            });
          });
        }
      });
    },
    doMfaActivate() {
      this.saving = true;
      this.userClient.mfaActivateRequest(MfaType.sms).then((response) => {
        this.saving = false;
        let baseMessage = 'MFA Code has been sent.';
        if (response && response.body && response.body.details) {
          baseMessage = response.body.message;
        }
        this.mfaActivateRequestText = `${baseMessage} Please enter code below.`;
      }).catch((e) => {
        this.saving = false;
        let extraText = 'Please try again!';
        if (e && e.body && e.body.error) {
          extraText = e.body.error;
        }
        this.$store.commit('setGlobalAlert', {
          msg: `Unable to activate MFA. ${extraText}`,
          error: true,
        });
      });
    },
    doMfaConfirm() {
      this.$validator.validateAll('mfa-confirm').then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;

          this.userClient.mfaActivateConfirm(MfaType.sms, this.mfaCodeSms).then(() => {
            this.saving = false;
            this.$store.commit('setGlobalAlert', {
              msg: 'MFA code confirmed!',
            });
            this.mfaCodeSms = '';
            this.populateActiveMfas();
          }).catch((e) => {
            this.saving = false;
            let extraText = 'Please try again!';
            if (e && e.body && e.body.error && e.body.error.message) {
              extraText = e.body.error.message;
            }
            this.$store.commit('setGlobalAlert', {
              msg: `Unable to validate MFA Code. ${extraText}`,
              error: true,
            });
          });
        }
      });
    },
    doMfaConfirmYubi() {
      this.$validator.validateAll('mfa-confirm-yubi').then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;

          this.userClient.mfaActivateRequest(MfaType.yubikey).then(() => {
            this.userClient.mfaActivateConfirm(MfaType.yubikey, this.mfaCodeYubi).then(() => {
              this.saving = false;
              this.$store.commit('setGlobalAlert', {
                msg: 'MFA code confirmed!',
              });

              this.mfaCodeYubi = '';
              this.populateActiveMfas();
            });
          }).catch((e) => {
            this.saving = false;
            let extraText = 'Please try again!';
            if (e && e.body && e.body.error && e.body.error.message) {
              extraText = e.body.error.message;
            }
            this.$store.commit('setGlobalAlert', {
              msg: `Unable to validate MFA Code. ${extraText}`,
              error: true,
            });
          });
        }
      });
    },
    doMfaActivateEmail() {
      this.saving = true;
      this.userClient.mfaActivateRequest(MfaType.email, { email: 'bug@test.com' }).then((response) => {
        this.saving = false;
        let baseMessage = 'Email Code has been sent.';
        if (response && response.body && response.body.details) {
          baseMessage = response.body.message;
        }
        this.mfaActivateEmailRequestText = `${baseMessage} Please enter code below.`;
      }).catch((e) => {
        this.saving = false;
        let extraText = 'Please try again!';
        if (e && e.body && e.body.error) {
          extraText = e.body.error;
        }
        this.$store.commit('setGlobalAlert', {
          msg: `Unable to activate MFA. ${extraText}`,
          error: true,
        });
      });
    },
    doMfaConfirmEmail() {
      this.$validator.validateAll('email-confirm').then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;

          this.userClient.mfaActivateConfirm(MfaType.email, this.mfaCodeEmail).then(() => {
            this.saving = false;
            this.$store.commit('setGlobalAlert', {
              msg: 'MFA code confirmed!',
            });
            this.mfaCodeEmail = '';
            this.populateActiveMfas();
          }).catch((e) => {
            this.saving = false;
            let extraText = 'Please try again!';
            if (e && e.body && e.body.error && e.body.error.message) {
              extraText = e.body.error.message;
            }
            this.$store.commit('setGlobalAlert', {
              msg: `Unable to validate MFA Code. ${extraText}`,
              error: true,
            });
          });
        }
      });
    },
    doMfaActivateApp() {
      this.saving = true;
      this.mfaQrCode = null;
      this.userClient.mfaActivateRequest(MfaType.app).then((response) => {
        this.saving = false;
        if (response && response.details) {
          this.mfaQrCode = response.details;
        }
        this.mfaActivateAppRequestText = 'Please scan the code in the authenticator application and enter the code for KGB Aviation.';
      }).catch((e) => {
        console.error(e);
        this.saving = false;
        let extraText = 'Please try again!';
        if (e && e.body && e.body.error) {
          extraText = e.body.error;
        }
        this.$store.commit('setGlobalAlert', {
          msg: `Unable to activate MFA. ${extraText}`,
          error: true,
        });
      });
    },
    doMfaConfirmApp() {
      this.$validator.validateAll('app-confirm').then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;

          this.userClient.mfaActivateConfirm(MfaType.app, this.mfaCodeApp).then(() => {
            this.saving = false;
            this.$store.commit('setGlobalAlert', {
              msg: 'MFA code confirmed!',
            });
            this.mfaCodeApp = '';
            this.populateActiveMfas();
          }).catch((e) => {
            this.saving = false;
            let extraText = 'Please try again!';
            if (e && e.body && e.body.error && e.body.error.message) {
              extraText = e.body.error.message;
            }
            this.$store.commit('setGlobalAlert', {
              msg: `Unable to validate MFA Code. ${extraText}`,
              error: true,
            });
          });
        }
      });
    },
    deactivateMfaMethod(method) {
      this.userClient.mfaDeactivateRequest(method).then(() => {
        this.$store.commit('setGlobalAlert', { msg: 'MFA method deactivated!' });
        this.populateActiveMfas();
      }).catch(() => {
        this.$store.commit('setGlobalAlert', {
          msg: 'Unable to deactivate MFA Method.',
          error: true,
        });
      });
    },
    changeActiveMethod(newMethod) {
      this.userClient.mfaMakePrimaryRequest(newMethod).then(() => {
        this.$store.commit('setGlobalAlert', { msg: 'MFA primary method updated!' });
        this.populateActiveMfas();
      }).catch(() => {
        this.$store.commit('setGlobalAlert', {
          msg: 'Unable to change MFA Method.',
          error: true,
        });
      });
    },
    populateActiveMfas() {
      this.userClient.getUserMfaMethods().then((mfaResponse) => {
        this.$store.commit('setUserMfa', mfaResponse);
        this.populatePrimaryMfaMethod();
      });
    },
    populatePrimaryMfaMethod() {
      if (this.$store.getters.mfaResponse && this.$store.getters.mfaResponse.results) {
        this.moreThanOneMfaMethod = this.$store.getters.mfaResponse.results.length > 1;
        const primaryMethod = this.$store.getters.mfaResponse.results.find(x => x.is_primary);
        if (primaryMethod) {
          this.primaryMfaMethod = primaryMethod.name;
        }
      }
    },
    cancelMfa() {
      this.mfaActivateRequestText = null;
      this.mfaActivateEmailRequestText = null;
      this.mfaActivateAppRequestText = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  @include border-radius($border-radius);

  margin-bottom: 30px;
  padding: 45px;
  border: solid 1px #ddd;

  @media (max-width: $sm-max) {
    margin-bottom: 20px;
    padding: 30px;
  }

  h4 {
    margin-bottom: 20px;
    font-size: 20px;
  }
}

.inline-alert {
  margin-bottom: 25px;
  padding: 14px;
  color: $white;
  background: $red;
}

.primary-method,
.second-method {
  display: inline;
  float: right;
  padding: 5px 14px;
  color: $white;
  font-weight: 100;
  font-size: small;
  background: $blue;
  border: 0;
  border-radius: 10px;
  outline: 0;
  box-shadow: none;
  user-select: none;
}

.ml-1 {
  margin-left: 10px;
}
</style>
